import { type ReactNode, useRef, useState } from "react";
import { Button } from "../../../components/Button/Button.tsx";
import { Icon, type IconName } from "../../../components/Icon/Icon.tsx";
import { Switch } from "../../../components/Switch/Switch.tsx";
import { useAppMutation } from "../../../http/useAppMutation.ts";
import { saveReturnPath } from "../../../utils/returnPath.ts";
import {
  SUBSCRIPTION_MONTHLY_PRICES,
  SUBSCRIPTION_YEARLY_PRICES,
} from "../../constants.ts";
import type {
  BillingPeriod,
  Workspace,
  WorkspaceDetails,
  WorkspaceType,
} from "../../types.ts";

export const PricingTable = ({
  workspace,
  workspaceDetails,
  highlight,
  onSubscriptionCheckoutUrlCreation,
  onClickContinueWithCurrentVersion,
}: {
  workspace: Workspace;
  workspaceDetails: WorkspaceDetails;
  highlight?: {
    type: WorkspaceType;
    label: string;
  };
  onSubscriptionCheckoutUrlCreation?: () => void;
  onClickContinueWithCurrentVersion?: () => void;
}) => {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  const selectedPlanRef = useRef<WorkspaceType | null>(null);

  const { mutate: createSubscriptionCheckoutUrl, isLoading } = useAppMutation<{
    url: string;
  }>({
    path: "payments/create-subscription-checkout-url",
    invalidate: [],
    onSuccess: (response) => {
      if (response.data.url) window.open(response.data.url, "_self");
      onSubscriptionCheckoutUrlCreation?.();
    },
  }).mutation;

  return (
    <div className="flex-col-center gap-400">
      <div className="flex-row justify-end w-full">
        <div className="flex-row-center gap-200">
          <div className="flex-col items-end">
            <div className="label-lg-semibold">Yearly</div>
            <div className="label-md-default">2 months free!</div>
          </div>
          <Switch
            value={billingPeriod === "yearly"}
            onChange={(checked) => {
              setBillingPeriod(checked ? "yearly" : "monthly");
            }}
          />
        </div>
      </div>
      <div className="flex-col gap-200">
        <div className="flex-row items-end gap-200">
          <PricingCard
            workspace={workspace}
            workspaceDetails={workspaceDetails}
            workspaceType="pro"
            billingPeriod={billingPeriod}
            description="Empowering freelancers with stunning, high-quality imagery."
            numSeats={1}
            features={[
              {
                // FIXME: these is not the real rate limit values, but rather some value that we invented
                // with the purpose of pushing people to pay for a better plan.
                // The real values are in the backend code, on the RateLimitManager.
                label: "200 on-brand generations per hour",
                icon: "Clock",
              },
              { label: "200 monthly credits", icon: "Coins" },
              { label: "Up to 6 watermark-free downloads" },
              { label: "Up to 10 styles, objects or characters" },
              { label: "1 user", icon: "User" },
              { label: "Chat assistance", icon: "MessageCircle" },
              { label: "No intellectual property liability", icon: "X" },
              {
                label: "$7.9 per extra watermark-free download",
                icon: "DollarSign",
              },
            ]}
            buttonVariant="secondary"
            loading={isLoading && selectedPlanRef.current === "pro"}
            onClick={() => {
              saveReturnPath();
              selectedPlanRef.current = "pro";
              createSubscriptionCheckoutUrl({
                workspace_uuid: workspace.uuid,
                workspace_type: "pro",
                billing_period: billingPeriod,
              });
            }}
            highlightLabel={
              highlight?.type === "pro" ? highlight.label : undefined
            }
          />
          <PricingCard
            workspace={workspace}
            workspaceDetails={workspaceDetails}
            workspaceType="team"
            billingPeriod={billingPeriod}
            description="Helping small brands shine with premium-quality visuals."
            numSeats={3}
            features={[
              {
                label: "Unlimited on-brand generations",
                icon: "Infinity",
              },
              { label: "2000 monthly credits", icon: "Coins" },
              { label: "Up to 66 watermark-free downloads" },
              { label: "Up to 100 styles, objects or characters" },
              { label: "3 users", icon: "Users" },
              { label: "Priority support", icon: "Headset" },
              {
                label: "Limited intellectual property liability",
                icon: "Check",
              },
              {
                label: "$5.9 per extra watermark-free download",
                icon: "DollarSign",
              },
            ]}
            buttonVariant="primary"
            loading={isLoading && selectedPlanRef.current === "team"}
            onClick={() => {
              saveReturnPath();
              selectedPlanRef.current = "team";
              createSubscriptionCheckoutUrl({
                workspace_uuid: workspace.uuid,
                workspace_type: "team",
                billing_period: billingPeriod,
              });
            }}
            highlightLabel={
              highlight?.type === "team" ? highlight.label : undefined
            }
          />
          <PricingCard
            workspace={workspace}
            workspaceDetails={workspaceDetails}
            workspaceType="enterprise"
            billingPeriod={billingPeriod}
            description="For brands and agencies with intense visual demands."
            features={[
              {
                label: "Unlimited on-brand generations",
                icon: "Infinity",
              },
              { label: "Tailored credits", icon: "Coins" },
              { label: "Tailored watermark-free downloads" },
              { label: "Tailored styles, objects or characters" },
              { label: "Tailored number of users", icon: "Users" },
              {
                label: "Priority assistance and onboarding",
                icon: "Headset",
              },
              { label: "Intellectual property liability", icon: "Check" },
              { label: "Tailored number of images", icon: "DollarSign" },
            ]}
            buttonVariant="primary"
            loading={isLoading && selectedPlanRef.current === "enterprise"}
            onClick={() => {
              selectedPlanRef.current = "enterprise";
              window.open(
                "https://discover.pimento.design/contact-us",
                "_blank",
              );
            }}
            highlightLabel={
              highlight?.type === "enterprise" ? highlight.label : undefined
            }
          />
        </div>
        {onClickContinueWithCurrentVersion && (
          <div className="flex-row w-full justify-end">
            <Button
              variant="secondary"
              onClick={onClickContinueWithCurrentVersion}
              iconName="ArrowRight"
              iconPosition="right"
            >
              Continue with the current version
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const PricingCard = ({
  workspace,
  workspaceDetails,
  workspaceType,
  billingPeriod,
  description,
  numSeats,
  buttonVariant,
  onClick,
  features,
  highlightLabel,
  loading,
}: {
  workspace: Workspace;
  workspaceDetails: WorkspaceDetails;
  workspaceType: WorkspaceType;
  billingPeriod: BillingPeriod;
  description: string;
  numSeats?: number;
  buttonVariant: "primary" | "secondary";
  onClick: () => void;
  features: {
    label: string;
    icon?: IconName;
  }[];
  loading: boolean;
  highlightLabel?: string;
}) =>
  highlightLabel ? (
    <HighligthPricingCardLayout label={highlightLabel}>
      <PricingCardContent
        workspace={workspace}
        workspaceDetails={workspaceDetails}
        workspaceType={workspaceType}
        billingPeriod={billingPeriod}
        description={description}
        numSeats={numSeats}
        buttonVariant={buttonVariant}
        onClick={onClick}
        features={features}
        loading={loading}
      />
    </HighligthPricingCardLayout>
  ) : (
    <PricingCardContent
      workspace={workspace}
      workspaceDetails={workspaceDetails}
      workspaceType={workspaceType}
      billingPeriod={billingPeriod}
      description={description}
      numSeats={numSeats}
      buttonVariant={buttonVariant}
      onClick={onClick}
      features={features}
      loading={loading}
    />
  );

const PricingCardContent = ({
  workspace,
  workspaceDetails,
  workspaceType,
  billingPeriod,
  description,
  numSeats,
  buttonVariant,
  onClick,
  features,
  loading,
}: {
  workspace: Workspace;
  workspaceDetails: WorkspaceDetails;
  workspaceType: WorkspaceType;
  billingPeriod: BillingPeriod;
  description: string;
  numSeats?: number;
  buttonVariant: "primary" | "secondary";
  onClick: () => void;
  loading: boolean;
  features: {
    label: string;
    icon?: IconName;
  }[];
}) => {
  const alreadySubscribed =
    workspace.type === workspaceType &&
    workspaceDetails.billing_period === billingPeriod;
  const workspaceTypeLabel =
    workspaceType.charAt(0).toUpperCase() + workspaceType.slice(1);
  const subscriptionMonthlyPrice = SUBSCRIPTION_MONTHLY_PRICES[workspaceType];
  const subscriptionYearlyPrice = SUBSCRIPTION_YEARLY_PRICES[workspaceType];
  const customPrice =
    subscriptionMonthlyPrice === undefined ||
    subscriptionYearlyPrice === undefined;
  const priceMonthly = customPrice
    ? undefined
    : billingPeriod === "monthly"
    ? subscriptionMonthlyPrice
    : subscriptionYearlyPrice / 12;
  const priceYearly = customPrice
    ? undefined
    : billingPeriod === "monthly"
    ? subscriptionMonthlyPrice * 12
    : subscriptionYearlyPrice;
  return (
    <div className="w-[300px] flex-col p-800 gap-400 rounded-300 bg-surface-primary-rest">
      <div className="heading-2xl">{workspaceTypeLabel}</div>
      <div className="label-lg-default">{description}</div>
      <div className="flex-col gap-200">
        {priceMonthly !== undefined && numSeats !== undefined ? (
          <div className="flex-row items-center gap-200">
            <div className="heading-4xl">${priceMonthly.toFixed(0)}</div>
            <div className="label-lg-default">/month</div>
          </div>
        ) : (
          <div className="heading-4xl">Let's talk</div>
        )}
        <div className="h-500 flex-row justify-between">
          {numSeats !== undefined && (
            <div className="label-lg-default">
              {numSeats} {numSeats > 1 ? "seats included" : "single seat"}
            </div>
          )}
          {billingPeriod === "yearly" && priceYearly !== undefined && (
            <div className="label-lg-default text-disabled">
              ${priceYearly.toFixed(0)} yearly
            </div>
          )}
        </div>
      </div>
      <Button
        disabled={alreadySubscribed}
        variant={buttonVariant}
        onClick={onClick}
        loading={loading}
      >
        {alreadySubscribed
          ? "Current plan"
          : customPrice
          ? "Contact us"
          : `Get Pimento ${workspaceTypeLabel}`}
      </Button>
      <div className="w-full h-[1px] bg-surface-transparent-primary-active" />
      <div className="flex-col gap-200">
        {features.map((feature, index) => (
          <div key={index} className="flex-row justify-start gap-200">
            {feature.icon ? (
              <Icon size="sm" name={feature.icon} />
            ) : (
              <div className="w-400 h-400" />
            )}
            <div className="label-md-default">{feature.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const HighligthPricingCardLayout = ({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) => (
  <div
    className="flex-col rounded-300 bg-brand-yellow"
    style={{
      boxShadow: "0px 0px 0px 4px var(--brand-yellow)",
    }}
  >
    <div className="flex-row-center gap-100 h-1200 label-lg-semibold text-primary">
      <Icon name="PimentoStar" size="xs" />
      {label}
    </div>
    {children}
  </div>
);
